/**
 * Shared Theme Definitions
 * Single source of truth for theme IDs, names, colors, and categories.
 * Used by both web (src/config/themes.ts) and mobile (SettingsScreen).
 */

export interface ThemeColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  secondary: string;
  secondaryHover: string;
  accent: string;
  accentHover: string;
  success: string;
  successHover: string;
  warning: string;
  warningHover: string;
  danger: string;
  dangerHover: string;
  info: string;
  infoHover: string;
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  surface: string;
  surfaceHover: string;
  surfaceActive: string;
  border: string;
  borderHover: string;
  borderFocus: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  glow: string;
  shimmer: string;
  /** Background of a button, chip or tab pill that carries a label. */
  primaryFill: string;
  /** That background on hover or press. */
  primaryFillHover: string;
  /** Text and icons drawn on primaryFill and primaryFillHover. */
  onPrimaryFill: string;
}

type PrimaryFillKey = 'primaryFill' | 'primaryFillHover' | 'onPrimaryFill';

/** Fill keys default to primary, primaryHover and textInverse; a theme passes only its overrides. */
function withPrimaryFill(
  base: Omit<ThemeColors, PrimaryFillKey>,
  overrides: Partial<Pick<ThemeColors, PrimaryFillKey>> = {},
): ThemeColors {
  return {
    ...base,
    primaryFill: overrides.primaryFill ?? base.primary,
    primaryFillHover: overrides.primaryFillHover ?? base.primaryHover,
    onPrimaryFill: overrides.onPrimaryFill ?? base.textInverse,
  };
}

/** Dark Electric and Midnight Blue keep white labels on a deeper blue than their primary. */
const DEEP_BLUE_FILL = {
  primaryFill: '#2563EB',
  primaryFillHover: '#1D4ED8',
  onPrimaryFill: '#FFFFFF',
};

export type ThemeCategory = 'core' | 'nba' | 'premium' | 'accessibility';

export interface SharedTheme {
  id: string;
  name: string;
  description: string;
  category: ThemeCategory;
  colors: ThemeColors;
}

export const THEME_CATEGORY_LABELS: Record<ThemeCategory, string> = {
  core: 'Core',
  nba: 'NBA Teams',
  premium: 'Premium',
  accessibility: 'Accessibility',
};

export const themes = {
  'dark-electric': {
    id: 'dark-electric',
    name: 'Dark Electric',
    description: 'Default theme with blue buttons and subtle accents',
    category: 'core',
    colors: withPrimaryFill(
      {
        primary: '#60A5FA',
        primaryHover: '#93C5FD',
        primaryLight: '#BFDBFE',
        secondary: '#9D4EDD',
        secondaryHover: '#B86EF7',
        accent: '#06FFA5',
        accentHover: '#26FFBB',
        success: '#10B981',
        successHover: '#34D399',
        warning: '#F59E0B',
        warningHover: '#FBBF24',
        danger: '#F87171',
        dangerHover: '#FCA5A5',
        info: '#06B6D4',
        infoHover: '#22D3EE',
        background: '#111827',
        backgroundSecondary: '#1F2937',
        backgroundTertiary: '#374151',
        surface: '#1F2937',
        surfaceHover: '#374151',
        surfaceActive: '#4B5563',
        border: '#374151',
        borderHover: '#4B5563',
        borderFocus: '#60A5FA',
        textPrimary: '#FFFFFF',
        textSecondary: '#D1D5DB',
        textTertiary: '#9CA3AF',
        textInverse: '#111827',
        glow: 'rgba(96, 165, 250, 0.3)',
        shimmer: 'rgba(96, 165, 250, 0.1)',
      },
      DEEP_BLUE_FILL,
    ),
  },

  'midnight-blue': {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    description: 'Professional blue theme with subtle accents',
    category: 'core',
    colors: withPrimaryFill(
      {
        primary: '#60A5FA',
        primaryHover: '#93C5FD',
        primaryLight: '#BFDBFE',
        secondary: '#8B5CF6',
        secondaryHover: '#A78BFA',
        accent: '#06B6D4',
        accentHover: '#22D3EE',
        success: '#10B981',
        successHover: '#34D399',
        warning: '#F59E0B',
        warningHover: '#FBBF24',
        danger: '#F87171',
        dangerHover: '#FCA5A5',
        info: '#06B6D4',
        infoHover: '#22D3EE',
        background: '#0F172A',
        backgroundSecondary: '#1E293B',
        backgroundTertiary: '#334155',
        surface: '#1E293B',
        surfaceHover: '#334155',
        surfaceActive: '#475569',
        border: '#334155',
        borderHover: '#475569',
        borderFocus: '#60A5FA',
        textPrimary: '#F8FAFC',
        textSecondary: '#CBD5E1',
        textTertiary: '#94A3B8',
        textInverse: '#0F172A',
        glow: 'rgba(96, 165, 250, 0.3)',
        shimmer: 'rgba(96, 165, 250, 0.1)',
      },
      DEEP_BLUE_FILL,
    ),
  },

  'sunset-amber': {
    id: 'sunset-amber',
    name: 'Sunset Amber',
    description: 'Warm amber and orange tones',
    category: 'core',
    colors: withPrimaryFill({
      primary: '#F59E0B',
      primaryHover: '#FBBF24',
      primaryLight: '#FCD34D',
      secondary: '#EA580C',
      secondaryHover: '#FB923C',
      accent: '#F97316',
      accentHover: '#FB923C',
      success: '#10B981',
      successHover: '#34D399',
      warning: '#F59E0B',
      warningHover: '#FBBF24',
      danger: '#F87171',
      dangerHover: '#FCA5A5',
      info: '#06B6D4',
      infoHover: '#22D3EE',
      background: '#1C1917',
      backgroundSecondary: '#292524',
      backgroundTertiary: '#44403C',
      surface: '#292524',
      surfaceHover: '#44403C',
      surfaceActive: '#57534E',
      border: '#44403C',
      borderHover: '#57534E',
      borderFocus: '#F59E0B',
      textPrimary: '#FFFFFF',
      textSecondary: '#D1D5DB',
      textTertiary: '#9CA3AF',
      textInverse: '#1C1917',
      glow: 'rgba(245, 158, 11, 0.3)',
      shimmer: 'rgba(245, 158, 11, 0.1)',
    }),
  },

  'violet-dream': {
    id: 'violet-dream',
    name: 'Violet Dream',
    description: 'Purple and pink gradient theme',
    category: 'core',
    colors: withPrimaryFill(
      {
        primary: '#C084FC',
        primaryHover: '#D8B4FE',
        primaryLight: '#E9D5FF',
        secondary: '#EC4899',
        secondaryHover: '#F472B6',
        accent: '#8B5CF6',
        accentHover: '#A78BFA',
        success: '#10B981',
        successHover: '#34D399',
        warning: '#F59E0B',
        warningHover: '#FBBF24',
        danger: '#FB7185',
        dangerHover: '#FDA4AF',
        info: '#06B6D4',
        infoHover: '#22D3EE',
        background: '#1E1B2E',
        backgroundSecondary: '#2D2640',
        backgroundTertiary: '#3D3352',
        surface: '#2D2640',
        surfaceHover: '#3D3352',
        surfaceActive: '#4D4064',
        border: '#3D3352',
        borderHover: '#4D4064',
        borderFocus: '#C084FC',
        textPrimary: '#FAF5FF',
        textSecondary: '#E9D5FF',
        textTertiary: '#D8B4FE',
        textInverse: '#1E1B2E',
        glow: 'rgba(192, 132, 252, 0.3)',
        shimmer: 'rgba(192, 132, 252, 0.1)',
      },
      { primaryFill: '#B46CF8', primaryFillHover: '#C084FC' },
    ),
  },

  'royal-flush': {
    id: 'royal-flush',
    name: 'Royal Flush',
    description: 'Regal purple meets gold luxury',
    category: 'nba',
    colors: withPrimaryFill({
      primary: '#FDB927',
      primaryHover: '#FFCA4D',
      primaryLight: '#FFDB73',
      secondary: '#FDB927',
      secondaryHover: '#FFCA4D',
      accent: '#FDB927',
      accentHover: '#FFCA4D',
      success: '#10B981',
      successHover: '#34D399',
      warning: '#F59E0B',
      warningHover: '#FBBF24',
      danger: '#F87171',
      dangerHover: '#FCA5A5',
      info: '#FDB927',
      infoHover: '#FFCA4D',
      background: '#1A0A2E',
      backgroundSecondary: '#2A1747',
      backgroundTertiary: '#3A2461',
      surface: '#2A1747',
      surfaceHover: '#3A2461',
      surfaceActive: '#4A317B',
      border: '#3A2461',
      borderHover: '#4A317B',
      borderFocus: '#FDB927',
      textPrimary: '#FFFFFF',
      textSecondary: '#E5D4FF',
      textTertiary: '#C5A4FF',
      textInverse: '#1A0A2E',
      glow: 'rgba(253, 185, 39, 0.3)',
      shimmer: 'rgba(85, 37, 131, 0.1)',
    }),
  },

  'lucky-streak': {
    id: 'lucky-streak',
    name: 'Lucky Streak',
    description: 'Championship green with golden accents',
    category: 'nba',
    colors: withPrimaryFill({
      primary: '#00B54D',
      primaryHover: '#00CC5C',
      primaryLight: '#00DD66',
      secondary: '#BA9653',
      secondaryHover: '#CAA663',
      accent: '#00A84F',
      accentHover: '#00C05B',
      success: '#00A84F',
      successHover: '#00C05B',
      warning: '#BA9653',
      warningHover: '#CAA663',
      danger: '#FF6B6B',
      dangerHover: '#FF8888',
      info: '#00B54D',
      infoHover: '#00CC5C',
      background: '#0A1F12',
      backgroundSecondary: '#0F2E1A',
      backgroundTertiary: '#143D22',
      surface: '#0F2E1A',
      surfaceHover: '#143D22',
      surfaceActive: '#194C2A',
      border: '#143D22',
      borderHover: '#194C2A',
      borderFocus: '#00B54D',
      textPrimary: '#FFFFFF',
      textSecondary: '#C7F4D9',
      textTertiary: '#9FE8B8',
      textInverse: '#0A1F12',
      glow: 'rgba(0, 181, 77, 0.3)',
      shimmer: 'rgba(186, 150, 83, 0.1)',
    }),
  },

  'golden-gate': {
    id: 'golden-gate',
    name: 'Golden Gate',
    description: 'Bay blue and gold skyline theme',
    category: 'nba',
    colors: withPrimaryFill({
      primary: '#5B8FE0',
      primaryHover: '#6B9CE8',
      primaryLight: '#7BAAF0',
      secondary: '#FFC72C',
      secondaryHover: '#FFD34C',
      accent: '#00A9E0',
      accentHover: '#00BFFF',
      success: '#10B981',
      successHover: '#34D399',
      warning: '#FFC72C',
      warningHover: '#FFD34C',
      danger: '#FF6B6B',
      dangerHover: '#FF8888',
      info: '#00A9E0',
      infoHover: '#00BFFF',
      background: '#0A1629',
      backgroundSecondary: '#0F2340',
      backgroundTertiary: '#143057',
      surface: '#0F2340',
      surfaceHover: '#143057',
      surfaceActive: '#193D6E',
      border: '#143057',
      borderHover: '#193D6E',
      borderFocus: '#5B8FE0',
      textPrimary: '#FFFFFF',
      textSecondary: '#C7E1FF',
      textTertiary: '#9FCFFF',
      textInverse: '#0A1629',
      glow: 'rgba(91, 143, 224, 0.3)',
      shimmer: 'rgba(255, 199, 44, 0.1)',
    }),
  },

  'arctic-cyan': {
    id: 'arctic-cyan',
    name: 'Arctic Cyan',
    description: 'Cool, analytical, premium tech',
    category: 'premium',
    colors: withPrimaryFill({
      primary: '#00D4FF',
      primaryHover: '#00BBEE',
      primaryLight: '#33DDFF',
      secondary: '#7C5CFC',
      secondaryHover: '#9680FD',
      accent: '#BFFF00',
      accentHover: '#CCFF33',
      success: '#00E68A',
      successHover: '#33FFAA',
      warning: '#FFD700',
      warningHover: '#FFE033',
      danger: '#FF3B5C',
      dangerHover: '#FF6680',
      info: '#7C5CFC',
      infoHover: '#9680FD',
      background: '#080C18',
      backgroundSecondary: '#121A2E',
      backgroundTertiary: '#1E2A44',
      surface: '#121A2E',
      surfaceHover: '#1A2540',
      surfaceActive: '#243050',
      border: '#1E2A44',
      borderHover: '#2A3654',
      borderFocus: '#00D4FF',
      textPrimary: '#FFFFFF',
      textSecondary: '#9FBACE',
      textTertiary: '#7A94B0',
      textInverse: '#080C18',
      glow: 'rgba(0, 212, 255, 0.3)',
      shimmer: 'rgba(124, 92, 252, 0.1)',
    }),
  },

  'crimson-edge': {
    id: 'crimson-edge',
    name: 'Crimson Edge',
    description: 'Bold, aggressive, high-energy',
    category: 'premium',
    colors: withPrimaryFill(
      {
        primary: '#FF2D78',
        primaryHover: '#E0256A',
        primaryLight: '#FF5C99',
        secondary: '#9945FF',
        secondaryHover: '#AD66FF',
        accent: '#00E5FF',
        accentHover: '#33EBFF',
        success: '#00E68A',
        successHover: '#33FFAA',
        warning: '#FFD700',
        warningHover: '#FFE033',
        danger: '#FF3B3B',
        dangerHover: '#FF6666',
        info: '#9945FF',
        infoHover: '#AD66FF',
        background: '#0B0D14',
        backgroundSecondary: '#171B28',
        backgroundTertiary: '#262B3C',
        surface: '#171B28',
        surfaceHover: '#1F2436',
        surfaceActive: '#292E42',
        border: '#262B3C',
        borderHover: '#343A4E',
        borderFocus: '#FF2D78',
        textPrimary: '#FFFFFF',
        textSecondary: '#B0B5CE',
        textTertiary: '#8A8FA8',
        textInverse: '#0B0D14',
        glow: 'rgba(255, 45, 120, 0.3)',
        shimmer: 'rgba(153, 69, 255, 0.1)',
      },
      { primaryFillHover: '#FF5C99' },
    ),
  },

  'emerald-prestige': {
    id: 'emerald-prestige',
    name: 'Emerald Prestige',
    description: 'Premium, sophisticated emerald & gold',
    category: 'premium',
    colors: withPrimaryFill({
      primary: '#00E68A',
      primaryHover: '#00CC7A',
      primaryLight: '#33FFAA',
      secondary: '#FFD700',
      secondaryHover: '#FFE033',
      accent: '#2E6FFF',
      accentHover: '#5088FF',
      success: '#00E68A',
      successHover: '#33FFAA',
      warning: '#FFD700',
      warningHover: '#FFE033',
      danger: '#FF3B5C',
      dangerHover: '#FF6680',
      info: '#2E6FFF',
      infoHover: '#5088FF',
      background: '#060D11',
      backgroundSecondary: '#0E1A20',
      backgroundTertiary: '#1A2830',
      surface: '#0E1A20',
      surfaceHover: '#152228',
      surfaceActive: '#1E2D35',
      border: '#1A2830',
      borderHover: '#253840',
      borderFocus: '#00E68A',
      textPrimary: '#FFFFFF',
      textSecondary: '#9EBCC8',
      textTertiary: '#7A9AA8',
      textInverse: '#060D11',
      glow: 'rgba(0, 230, 138, 0.3)',
      shimmer: 'rgba(255, 215, 0, 0.1)',
    }),
  },

  'high-contrast': {
    id: 'high-contrast',
    name: 'High Contrast',
    description: 'Maximum contrast (WCAG AAA) for accessibility',
    category: 'accessibility',
    colors: withPrimaryFill({
      primary: '#00FFFF',
      primaryHover: '#33FFFF',
      primaryLight: '#66FFFF',
      secondary: '#FF00FF',
      secondaryHover: '#FF33FF',
      accent: '#BFFF00',
      accentHover: '#CCFF33',
      success: '#00FF00',
      successHover: '#33FF33',
      warning: '#FFFF00',
      warningHover: '#FFFF33',
      danger: '#FF0000',
      dangerHover: '#FF3333',
      info: '#00FFFF',
      infoHover: '#33FFFF',
      background: '#000000',
      backgroundSecondary: '#0A0A0A',
      backgroundTertiary: '#141414',
      surface: '#0A0A0A',
      surfaceHover: '#141414',
      surfaceActive: '#1E1E1E',
      border: '#FFFFFF',
      borderHover: '#00FFFF',
      borderFocus: '#FFFF00',
      textPrimary: '#FFFFFF',
      textSecondary: '#E0E0E0',
      textTertiary: '#C0C0C0',
      textInverse: '#000000',
      glow: 'rgba(0, 255, 255, 0.5)',
      shimmer: 'rgba(191, 255, 0, 0.2)',
    }),
  },

  'light-court': {
    id: 'light-court',
    name: 'Light Court',
    description: 'Bright daytime theme with excellent contrast',
    category: 'accessibility',
    colors: withPrimaryFill({
      primary: '#1D4ED8',
      primaryHover: '#1E40AF',
      primaryLight: '#3B82F6',
      secondary: '#7C3AED',
      secondaryHover: '#6D28D9',
      accent: '#047857',
      accentHover: '#065F46',
      success: '#047857',
      successHover: '#065F46',
      warning: '#B45309',
      warningHover: '#92400E',
      danger: '#DC2626',
      dangerHover: '#B91C1C',
      info: '#0284C7',
      infoHover: '#0369A1',
      background: '#FFFFFF',
      backgroundSecondary: '#F9FAFB',
      backgroundTertiary: '#F3F4F6',
      surface: '#FFFFFF',
      surfaceHover: '#F9FAFB',
      surfaceActive: '#F3F4F6',
      border: '#E5E7EB',
      borderHover: '#D1D5DB',
      borderFocus: '#1D4ED8',
      textPrimary: '#111827',
      textSecondary: '#4B5563',
      textTertiary: '#6B7280',
      textInverse: '#FFFFFF',
      glow: 'rgba(29, 78, 216, 0.15)',
      shimmer: 'rgba(124, 58, 237, 0.1)',
    }),
  },
} satisfies Record<string, SharedTheme>;

export type ThemeId = keyof typeof themes;

export const DEFAULT_THEME_ID: ThemeId = 'dark-electric';

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && Object.hasOwn(themes, value);
}

export function getSharedTheme(themeId: string): SharedTheme {
  return themes[themeId as ThemeId] || themes[DEFAULT_THEME_ID];
}

export function getThemeList(): SharedTheme[] {
  return Object.values(themes);
}

export function getThemesByCategory(category: ThemeCategory): SharedTheme[] {
  return Object.values(themes).filter((t) => t.category === category);
}
